/**
 * Lyst/mørkt tema for rammeverket (topplinje, paneler, statuslinje). 3D-viewporten er alltid mørk (datafarger).
 * Standard følger systemet (prefers-color-scheme); et eksplisitt valg huskes i localStorage (kun bekvemmelighet).
 */

export type ThemePref = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';
export const THEME_KEY = 'gk-viewer-theme';

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function store(): Store | undefined {
  try { return localStorage; } catch { return undefined; }
}

export function readThemePref(s: Store | undefined = store()): ThemePref {
  try {
    const v = s?.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(pref: ThemePref, systemDark: boolean): Theme {
  return pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
}

/** Bryteren går alltid til motsatt av det som vises nå, og lagrer valget. */
export function toggleTheme(current: Theme, s: Store | undefined = store()): Theme {
  const next: Theme = current === 'dark' ? 'light' : 'dark';
  try { s?.setItem(THEME_KEY, next); } catch { /* privat modus – ignorer */ }
  return next;
}

const systemDark = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;

/** Setter data-theme på <html> (CSS-tokenene leser den) og returnerer temaet som faktisk vises. */
export function applyTheme(pref: ThemePref = readThemePref(), doc: Document = document): Theme {
  const theme = resolveTheme(pref, systemDark());
  doc.documentElement.dataset.theme = theme;
  doc.documentElement.style.colorScheme = theme;
  return theme;
}

export function currentTheme(doc: Document = document): Theme {
  return doc.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}
